// The deals list's query state, as URL search params.
//
// Keeping it in the URL rather than in component state is what makes a filtered
// view shareable — "the two USD deals in contracting, by renewal date" is a
// link, and a browser Back button walks the filter history. The cost is that
// anything can arrive in the URL, so this parses defensively: an unreadable
// param falls back to the default rather than throwing, because a hand-edited
// URL should show the deals list, not an error boundary.
//
// TWO THINGS MAKE THAT FALLBACK REAL, and neither is the route's
// `validateSearch` on its own:
//
//   * Every field carries its own `.catch(undefined)`, so ONE unreadable param
//     falls back alone instead of taking the rest of the URL with it. A
//     whole-object `safeParse` that returns `{}` on failure turns a mistyped
//     state into a list with no currency filter either.
//   * The parse happens AGAIN in `searchToQueryState`, at the point of use.
//     TanStack Router merges each route's validated search over its PARENT's,
//     and `rootRoute` validates nothing — so a value this schema rejected still
//     reaches the component. Before that second parse, `?page=0` became
//     `offset=-25` on the wire and a bare `?state=won` was spread character by
//     character into `state=w&state=o&state=n`. Both were 400s from a link
//     someone could type. `billingSearch.ts` fixed the same defect first; this
//     file follows it.

import { z } from "zod";
import { CountryCode, Currency, DEAL_SORT_FIELDS, DealState, Uuid } from "@crm/shared";
import { parseSort } from "../../api/queryString.js";
import { isCalendarDate } from "../../format/calendarDate.js";
import { DEFAULT_MEASURE, samePeriod, type Measure, type Period } from "../sales/salesModel.js";
import { SPLITS, defaultChangesPeriod, type Split } from "./changesModel.js";
import {
  DEAL_LOCATION_FILTERS,
  DEAL_RENEWAL_WINDOWS,
  DEAL_START_WINDOWS,
  DEFAULT_DEALS_QUERY,
  type DealsQueryState,
} from "../../api/queries.js";
import { BOARD_MEASURES, type BoardMeasure } from "./boardModel.js";

const SortField = z.enum(DEAL_SORT_FIELDS);

/**
 * One value or several, exactly as `DealListQuery` in the contract spells the
 * same filter. A repeated param is what the app's own links produce; a bare
 * `?state=won` is what a person types, and it means the same thing.
 */
const StateFilter = z.union([DealState, z.array(DealState)]);

const LocationFilter = z.enum(DEAL_LOCATION_FILTERS);
const StartWindow = z.enum(DEAL_START_WINDOWS);
const RenewalWindow = z.enum(DEAL_RENEWAL_WINDOWS);

/**
 * List or board. A view MODE rather than a route of its own, for the reason
 * `billingSearch.ts` gives for the billing screen: both views show the same
 * filtered set of deals, so the filters have to be one URL that either view
 * reads — switching to the board must not drop the search someone just typed.
 */
export const DEALS_VIEW_MODES = ["list", "board", "changes"] as const;
export const DealsViewMode = z.enum(DEALS_VIEW_MODES);
export type DealsViewMode = z.infer<typeof DealsViewMode>;

/** Which figure the board's columns add up — see `BOARD_MEASURES`. */
const BoardMeasureParam = z.enum(BOARD_MEASURES);

const IsoDay = z.string().refine(isCalendarDate);
const SplitParam = z.enum(SPLITS as [Split, ...Split[]]);

const DealsSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  state: StateFilter.optional().catch(undefined),
  /** A partner's family, by the partner's id. */
  partner_id: Uuid.optional().catch(undefined),
  currency: Currency.optional().catch(undefined),
  /** The floor on the win probability, 0-100. */
  win: z.number().int().min(0).max(100).optional().catch(undefined),
  start: StartWindow.optional().catch(undefined),
  renewal: RenewalWindow.optional().catch(undefined),
  /**
   * The alpha-2 CODE, validated here rather than passed through: "Canada" in
   * the URL would reach the API as a filter matching no deal, and the empty
   * list that came back would look like an answer rather than a typo.
   */
  country: CountryCode.optional().catch(undefined),
  /** Which enrichment worklist, if any — see `DEAL_LOCATION_FILTERS`. */
  location: LocationFilter.optional().catch(undefined),
  /** `field:asc|desc`, the same spelling the API takes. */
  sort: z.string().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  /** Which view shows the deals — see `DealsViewMode`. */
  view: DealsViewMode.optional().catch(undefined),
  /** Which figure the board's columns add up. Meaningless to the list, carried through untouched. */
  value: BoardMeasureParam.optional().catch(undefined),
  /**
   * The changes view's own state, prefixed so no other key can mean something
   * else by the same name. Carried through the other views untouched.
   */
  ch_from: IsoDay.optional().catch(undefined),
  ch_to: IsoDay.optional().catch(undefined),
  ch_basis: z.enum(["gross", "our_share"]).optional().catch(undefined),
  ch_weighted: z.enum(["yes", "no"]).optional().catch(undefined),
  /** What each step's bar is stacked from. */
  ch_by: SplitParam.optional().catch(undefined),
});

export type DealsSearch = z.infer<typeof DealsSearchSchema>;

export function validateDealsSearch(raw: Record<string, unknown>): DealsSearch {
  const parsed = DealsSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/** One state or several, as one list. */
function statesOf(raw: DealsSearch["state"]): readonly DealState[] {
  if (raw === undefined) return DEFAULT_DEALS_QUERY.states;
  return Array.isArray(raw) ? raw : [raw];
}

/**
 * `raw`, not a trusted `DealsSearch`, and re-validated here — see the note at
 * the top of this file for why the route's own `validateSearch` is not enough.
 */
export function searchToQueryState(raw: DealsSearch): DealsQueryState {
  const search = validateDealsSearch(raw as Record<string, unknown>);
  const sort = search.sort === undefined ? null : parseSort(search.sort);
  const sortIsKnown = sort !== null && SortField.safeParse(sort.field).success;

  return {
    q: search.q ?? DEFAULT_DEALS_QUERY.q,
    states: statesOf(search.state),
    partnerId: search.partner_id ?? DEFAULT_DEALS_QUERY.partnerId,
    currency: search.currency ?? DEFAULT_DEALS_QUERY.currency,
    winProbMin: search.win ?? DEFAULT_DEALS_QUERY.winProbMin,
    start: search.start ?? DEFAULT_DEALS_QUERY.start,
    renewal: search.renewal ?? DEFAULT_DEALS_QUERY.renewal,
    country: search.country ?? DEFAULT_DEALS_QUERY.country,
    location: search.location ?? DEFAULT_DEALS_QUERY.location,
    sort: sortIsKnown ? sort : DEFAULT_DEALS_QUERY.sort,
    page: search.page ?? DEFAULT_DEALS_QUERY.page,
    pageSize: DEFAULT_DEALS_QUERY.pageSize,
  };
}

/** Defaults are omitted so an unfiltered list has a clean `/deals` URL. */
export function queryStateToSearch(state: DealsQueryState): DealsSearch {
  const sort = `${state.sort.field}:${state.sort.direction}`;
  const defaultSort = `${DEFAULT_DEALS_QUERY.sort.field}:${DEFAULT_DEALS_QUERY.sort.direction}`;

  return {
    ...(state.q === "" ? {} : { q: state.q }),
    ...(state.states.length === 0 ? {} : { state: [...state.states] }),
    ...(state.partnerId === null ? {} : { partner_id: state.partnerId }),
    ...(state.currency === null ? {} : { currency: state.currency }),
    ...(state.winProbMin === null ? {} : { win: state.winProbMin }),
    ...(state.start === "any" ? {} : { start: state.start }),
    ...(state.renewal === "any" ? {} : { renewal: state.renewal }),
    ...(state.country === null ? {} : { country: state.country }),
    ...(state.location === "any" ? {} : { location: state.location }),
    ...(sort === defaultSort ? {} : { sort }),
    ...(state.page === 1 ? {} : { page: state.page }),
  };
}

/**
 * Which view the URL asks for. Re-validated at the point of use, like the query
 * half: `?view=gantt` is the board, not an error boundary. The BOARD is the
 * default — the pipeline by stage is what this screen is opened for.
 */
export function searchToViewMode(raw: DealsSearch): DealsViewMode {
  const search = validateDealsSearch(raw as Record<string, unknown>);
  return search.view ?? "board";
}

/** The board is the default, so it leaves the URL clean; the list is `?view=list`. */
export function viewModeToSearch(view: DealsViewMode): Pick<DealsSearch, "view"> {
  return view === "board" ? {} : { view };
}

/** Which figure the board adds up; contracted unless the URL says otherwise. */
export function searchToBoardMeasure(raw: DealsSearch): BoardMeasure {
  const search = validateDealsSearch(raw as Record<string, unknown>);
  return search.value ?? "contracted";
}

/** Contracted is the default, so it leaves the URL clean. */
export function boardMeasureToSearch(measure: BoardMeasure): Pick<DealsSearch, "value"> {
  return measure === "contracted" ? {} : { value: measure };
}

/** The changes view: which period, measured how, and stacked by deal or by partner. */
export interface ChangesState {
  readonly period: Period;
  readonly measure: Measure;
  readonly split: Split;
}

export function searchToChangesState(raw: DealsSearch, today: string): ChangesState {
  const search = validateDealsSearch(raw as Record<string, unknown>);
  return {
    period:
      search.ch_from === undefined || (search.ch_to !== undefined && search.ch_to < search.ch_from)
        ? defaultChangesPeriod(today)
        : { from: search.ch_from, to: search.ch_to ?? null },
    measure: {
      basis: search.ch_basis ?? DEFAULT_MEASURE.basis,
      weighted:
        search.ch_weighted === undefined ? DEFAULT_MEASURE.weighted : search.ch_weighted === "yes",
    },
    split: search.ch_by ?? "deal",
  };
}

/** Only what differs from the default goes in the URL. */
export function changesStateToSearch(
  state: ChangesState,
  today: string,
): Pick<DealsSearch, "ch_from" | "ch_to" | "ch_basis" | "ch_weighted" | "ch_by"> {
  return {
    ...(samePeriod(state.period, defaultChangesPeriod(today))
      ? {}
      : {
          ch_from: state.period.from,
          ...(state.period.to === null ? {} : { ch_to: state.period.to }),
        }),
    ...(state.measure.basis === DEFAULT_MEASURE.basis ? {} : { ch_basis: state.measure.basis }),
    ...(state.measure.weighted === DEFAULT_MEASURE.weighted
      ? {}
      : { ch_weighted: state.measure.weighted ? "yes" : "no" }),
    ...(state.split === "deal" ? {} : { ch_by: state.split }),
  };
}
